import React, { useState, useEffect, useMemo } from "react";
import { getToken } from "../utils/auth";
import "./CampusStudents.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

export default function CampusStudents() {
  const [campuses, setCampuses] = useState([]);
  const [selectedCollege, setSelectedCollege] = useState("");
  const [students, setStudents] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingCampuses, setLoadingCampuses] = useState(true);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [error, setError] = useState(null);
  const [actionSuccess, setActionSuccess] = useState(null);

  // Edit Modal State
  const [editingStudent, setEditingStudent] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: "",
    email: "",
    college: "",
    year: "",
    batch: "",
    regNo: "",
    phNo: "",
    leetcodeURL: ""
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [modalError, setModalError] = useState(null);

  // Dynamic Options for Modal
  const [modalYears, setModalYears] = useState([]);
  const [modalBatches, setModalBatches] = useState([]);
  const [loadingYears, setLoadingYears] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(false);

  // Helper to build headers with auth token
  const getAuthHeaders = () => {
    const token = getToken();
    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
  };

  // Fetch campus list on mount
  useEffect(() => {
    const fetchCampuses = async () => {
      try {
        setLoadingCampuses(true);
        const response = await fetch(`${API_BASE_URL}/campus/get`, {
          method: "GET",
          headers: getAuthHeaders()
        });

        if (!response.ok) throw new Error("Failed to fetch campus list");
        const data = await response.json();
        setCampuses(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoadingCampuses(false);
      }
    };

    fetchCampuses();
  }, []);

  // Fetch students on campus selection change
  useEffect(() => {
    if (!selectedCollege) {
      setStudents([]);
      return;
    }

    const fetchStudents = async () => {
      try {
        setLoadingStudents(true);
        setError(null);
        setActionSuccess(null);
        const response = await fetch(
          `${API_BASE_URL}/api/students/college/${encodeURIComponent(selectedCollege)}`,
          {
            method: "GET",
            headers: getAuthHeaders()
          }
        );

        if (!response.ok) throw new Error("Failed to fetch students");
        const data = await response.json();
        setStudents(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoadingStudents(false);
      }
    };

    fetchStudents();
  }, [selectedCollege]);

  // Helper: Fetch Years for a given campus (matches Year response: [{ _id, Campusname, Year }])
  const fetchYearsForCampus = async (collegeName) => {
    if (!collegeName || typeof collegeName !== "string" || !collegeName.trim()) {
      setModalYears([]);
      return [];
    }
    try {
      setLoadingYears(true);
      const response = await fetch(
        `${API_BASE_URL}/year/get-by-campus?campus=${encodeURIComponent(collegeName.trim())}`,
        {
          method: "GET",
          headers: getAuthHeaders()
        }
      );
      if (!response.ok) throw new Error("Failed to fetch years");
      const data = await response.json();
      const list = Array.isArray(data) ? data : [];
      setModalYears(list);
      return list;
    } catch (err) {
      console.error(err);
      setModalYears([]);
      return [];
    } finally {
      setLoadingYears(false);
    }
  };

  // Helper: Fetch Batches for a given campus + year
  const fetchBatchesForCampusYear = async (collegeName, yearVal) => {
    if (!collegeName || !yearVal) {
      setModalBatches([]);
      return [];
    }
    try {
      setLoadingBatches(true);
      const response = await fetch(
        `${API_BASE_URL}/batch/get-by-campus-year?campus=${encodeURIComponent(
          String(collegeName).trim()
        )}&year=${encodeURIComponent(String(yearVal).trim())}`,
        {
          method: "GET",
          headers: getAuthHeaders()
        }
      );
      if (!response.ok) throw new Error("Failed to fetch batches");
      const data = await response.json();
      const list = Array.isArray(data) ? data : [];
      setModalBatches(list);
      return list;
    } catch (err) {
      console.error(err);
      setModalBatches([]);
      return [];
    } finally {
      setLoadingBatches(false);
    }
  };

  // Open modal with safe fallbacks and sequential year/batch loading
  const handleOpenEditModal = async (student) => {
    const studentCollege =
      (student && student.college) ||
      selectedCollege ||
      (campuses.length > 0 ? campuses[0].college : "");

    const studentYear =
      student.year !== undefined && student.year !== null ? String(student.year) : "";
    const studentBatch = student.batch || "";

    setEditingStudent(student);
    setEditFormData({
      name: student.name || "",
      email: student.email || "",
      college: studentCollege,
      year: studentYear,
      batch: studentBatch,
      regNo: student.regNo || "",
      phNo: student.phNo || "",
      leetcodeURL: student.leetcodeURL || ""
    });
    setModalError(null);

    // 1. Fetch Years for this campus
    if (studentCollege) {
      await fetchYearsForCampus(studentCollege);
      // 2. Fetch Batches if year is defined
      if (studentYear) {
        await fetchBatchesForCampusYear(studentCollege, studentYear);
      } else {
        setModalBatches([]);
      }
    } else {
      setModalYears([]);
      setModalBatches([]);
    }
  };

  const handleCloseEditModal = () => {
    setEditingStudent(null);
    setModalYears([]);
    setModalBatches([]);
    setModalError(null);
  };

  // Form field change with dependent cascading updates
  const handleFormChange = async (e) => {
    const { name, value } = e.target;

    if (name === "college") {
      const selectedCollegeVal = value || "";
      setEditFormData((prev) => ({
        ...prev,
        college: selectedCollegeVal,
        year: "",
        batch: ""
      }));
      setModalBatches([]);
      if (selectedCollegeVal) {
        await fetchYearsForCampus(selectedCollegeVal);
      } else {
        setModalYears([]);
      }
    } else if (name === "year") {
      const selectedYearVal = value || "";
      setEditFormData((prev) => ({
        ...prev,
        year: selectedYearVal,
        batch: ""
      }));
      if (selectedYearVal && editFormData.college) {
        await fetchBatchesForCampusYear(editFormData.college, selectedYearVal);
      } else {
        setModalBatches([]);
      }
    } else {
      setEditFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Client-side search filtering
  const filteredStudents = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return students;

    return students.filter((student) => {
      const name = (student.name || "").toLowerCase();
      const regNo = (student.regNo || "").toLowerCase();
      const email = (student.email || "").toLowerCase();
      const phNo = (student.phNo || "").toLowerCase();
      return (
        name.includes(query) ||
        regNo.includes(query) ||
        email.includes(query) ||
        phNo.includes(query)
      );
    });
  }, [students, searchQuery]);

  // Submit edit to PUT /api/students/:id
  const handleUpdateStudent = async (e) => {
    e.preventDefault();
    if (!editingStudent) return;

    const finalCollege = editFormData.college || selectedCollege || editingStudent.college;
    if (!finalCollege) {
      setModalError("College is required. Please select a college.");
      return;
    }

    try {
      setIsUpdating(true);
      setModalError(null);

      const payload = {
        name: editFormData.name.trim(),
        email: editFormData.email.trim(),
        college: finalCollege.trim(),
        batch: editFormData.batch || "",
        regNo: editFormData.regNo || "",
        phNo: editFormData.phNo || "",
        leetcodeURL: editFormData.leetcodeURL || ""
      };

      if (editFormData.year !== "") {
        payload.year = Number(editFormData.year);
      }

      const response = await fetch(`${API_BASE_URL}/api/students/${editingStudent._id}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message || "Failed to update student");
      }

      if (result.student.college !== selectedCollege) {
        setStudents((prev) => prev.filter((s) => s._id !== editingStudent._id));
      } else {
        setStudents((prev) =>
          prev.map((s) => (s._id === editingStudent._id ? { ...s, ...result.student } : s))
        );
      }

      setActionSuccess("Student details updated successfully.");
      handleCloseEditModal();
    } catch (err) {
      setModalError(err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  // Delete student with DELETE /api/students/:id
  const handleDeleteStudent = async (studentId, studentName) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${studentName || "this student"}? This action cannot be undone.`
    );
    if (!confirmDelete) return;

    try {
      setError(null);
      setActionSuccess(null);

      const response = await fetch(`${API_BASE_URL}/api/students/${studentId}`, {
        method: "DELETE",
        headers: getAuthHeaders()
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message || "Failed to delete student");
      }

      setStudents((prev) => prev.filter((s) => s._id !== studentId));
      setActionSuccess("Student deleted successfully.");
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="container">
      <header className="header">
        <h1>Campus Student Directory</h1>
        <p>Manage, search, and edit registered students across campuses</p>
      </header>

      {/* Campus Selector & Search */}
      <section className="controls-bar">
        <div className="control-group">
          <label htmlFor="campusSelect">Campus:</label>
          {loadingCampuses ? (
            <span className="loading-text">Loading campuses...</span>
          ) : (
            <select
              id="campusSelect"
              value={selectedCollege}
              onChange={(e) => {
                setSelectedCollege(e.target.value);
                setSearchQuery("");
              }}
              className="dropdown"
            >
              <option value="">-- Choose a College --</option>
              {campuses.map((campus) => (
                <option key={campus._id || campus.college} value={campus.college}>
                  {campus.college}
                </option>
              ))}
            </select>
          )}
        </div>

        {selectedCollege && (
          <div className="control-group search-group">
            <input
              type="text"
              placeholder="Search by name, reg no, email, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button
                type="button"
                className="clear-search-btn"
                onClick={() => setSearchQuery("")}
                aria-label="Clear search"
              >
                &times;
              </button>
            )}
          </div>
        )}
      </section>

      {/* Alerts */}
      {error && <div className="banner error-banner">{error}</div>}
      {actionSuccess && <div className="banner success-banner">{actionSuccess}</div>}

      {/* Main Student Cards */}
      <main className="content-area">
        {loadingStudents && (
          <div className="banner loading-banner">Fetching student records...</div>
        )}

        {!loadingStudents && selectedCollege && students.length === 0 && (
          <div className="banner empty-banner">
            No students found enrolled under <strong>{selectedCollege}</strong>.
          </div>
        )}

        {!loadingStudents && selectedCollege && students.length > 0 && filteredStudents.length === 0 && (
          <div className="banner empty-banner">
            No students match your search criteria &quot;{searchQuery}&quot;.
          </div>
        )}

        {!loadingStudents && !selectedCollege && (
          <div className="banner prompt-banner">
            Please pick a campus from the dropdown above to view student data.
          </div>
        )}

        {!loadingStudents && filteredStudents.length > 0 && (
          <div className="student-grid">
            {filteredStudents.map((student) => (
              <article key={student._id} className="student-card">
                <div className="card-header">
                  <div>
                    <h2 className="student-name">{student.name || "N/A"}</h2>
                    <span className="student-reg">{student.regNo || "No Reg No"}</span>
                  </div>
                  <span className={`badge badge-${student.role || "user"}`}>
                    {student.role || "user"}
                  </span>
                </div>

                <div className="card-body">
                  <div className="data-row">
                    <span className="data-label">Email:</span>
                    <span className="data-val">{student.email}</span>
                  </div>
                  <div className="data-row">
                    <span className="data-label">Phone:</span>
                    <span className="data-val">{student.phNo || "N/A"}</span>
                  </div>
                  <div className="data-row">
                    <span className="data-label">Year / Batch:</span>
                    <span className="data-val">
                      {student.year ? `Year ${student.year}` : "N/A"} &bull; {student.batch || "N/A"}
                    </span>
                  </div>

                  {student.leetcodeURL && (
                    <div className="data-row">
                      <span className="data-label">LeetCode:</span>
                      <span className="data-val">
                        <a
                          href={student.leetcodeURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="link"
                        >
                          Profile Link
                        </a>
                      </span>
                    </div>
                  )}

                  {/* Academic Metrics Summary */}
                  <div className="metrics-bar">
                    <div className="metric">
                      <span className="metric-count">{student.course?.length || 0}</span>
                      <span className="metric-label">Courses</span>
                    </div>
                    <div className="metric">
                      <span className="metric-count">{student.assignments?.length || 0}</span>
                      <span className="metric-label">Assignments</span>
                    </div>
                    <div className="metric">
                      <span className="metric-count">{student.mcqs?.length || 0}</span>
                      <span className="metric-label">MCQs</span>
                    </div>
                    <div className="metric">
                      <span className="metric-count">{student.task?.length || 0}</span>
                      <span className="metric-label">Tasks</span>
                    </div>
                  </div>
                </div>

                <div className="card-actions">
                  <button
                    type="button"
                    className="btn btn-edit"
                    onClick={() => handleOpenEditModal(student)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn-delete"
                    onClick={() => handleDeleteStudent(student._id, student.name)}
                  >
                    Delete
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>

      {/* Edit Student Modal */}
      {editingStudent && (
        <div className="modal-overlay" onClick={handleCloseEditModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Student Details</h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={handleCloseEditModal}
              >
                &times;
              </button>
            </div>

            {modalError && <div className="banner error-banner">{modalError}</div>}

            <form onSubmit={handleUpdateStudent} className="modal-form">
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  name="name"
                  value={editFormData.name}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={editFormData.email}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>College / Campus</label>
                  <select
                    name="college"
                    value={editFormData.college}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="">-- Choose College --</option>
                    {campuses.map((c) => (
                      <option key={c._id || c.college} value={c.college}>
                        {c.college}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>
                    Year {loadingYears && <span className="helper-hint">(Loading...)</span>}
                  </label>
                  <select
                    name="year"
                    value={editFormData.year}
                    onChange={handleFormChange}
                    disabled={!editFormData.college || loadingYears}
                  >
                    <option value="">-- Choose Year --</option>
                    {modalYears.map((item) => {
                      // Supports item.Year (capitalized schema), item.year, or bare scalar
                      const yrVal =
                        item.Year !== undefined
                          ? item.Year
                          : item.year !== undefined
                          ? item.year
                          : item;
                      const yrId = item._id || String(yrVal);
                      return (
                        <option key={yrId} value={yrVal}>
                          Year {yrVal}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>
                    Batch {loadingBatches && <span className="helper-hint">(Loading...)</span>}
                  </label>
                  <select
                    name="batch"
                    value={editFormData.batch}
                    onChange={handleFormChange}
                    disabled={!editFormData.year || loadingBatches}
                  >
                    <option value="">-- Choose Batch --</option>
                    {modalBatches.map((b) => {
                      // Supports b.Batch, b.batch, b.name, or bare string
                      const batchVal =
                        b.Batch !== undefined
                          ? b.Batch
                          : b.batch !== undefined
                          ? b.batch
                          : b.name || b;
                      const batchId = b._id || String(batchVal);
                      return (
                        <option key={batchId} value={batchVal}>
                          {batchVal}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="form-group">
                  <label>Registration No</label>
                  <input
                    type="text"
                    name="regNo"
                    value={editFormData.regNo}
                    onChange={handleFormChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    name="phNo"
                    value={editFormData.phNo}
                    onChange={handleFormChange}
                  />
                </div>

                <div className="form-group">
                  <label>LeetCode URL</label>
                  <input
                    type="url"
                    name="leetcodeURL"
                    placeholder="https://leetcode.com/username"
                    value={editFormData.leetcodeURL}
                    onChange={handleFormChange}
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseEditModal}
                  disabled={isUpdating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isUpdating}
                >
                  {isUpdating ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}